import React from 'react';
import { ISanityReviews } from '../../fragments/sanity-reviews-parts';
import { classNames } from '../../utils/classnames';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import Carousel from 'nuka-carousel';
import { useTailwindConfig, useMediaQuery } from '../../hooks';
import { Stars } from '../module-elements/stars';
import { StaticImage } from 'gatsby-plugin-image';

interface ReviewsProps {
  reviews: ISanityReviews;
  title: string;
}

function Reviews({ reviews, title }: ReviewsProps): JSX.Element {
  if (title === 'Corporate Events') {
    return <ReviewsCorporate reviews={reviews} title={title} />;
  }
  return <div></div>;
}

const ReviewsBooking = ({ reviews, title }: ReviewsProps): JSX.Element => {
  const tailwindConfig = useTailwindConfig();
  const isMedium = useMediaQuery(
    `(min-width: ${tailwindConfig.theme.screens.md})`
  );
  const isSm = useMediaQuery(`(min-width: ${tailwindConfig.theme.screens.sm})`);
  const isLarge = useMediaQuery(
    `(min-width: ${tailwindConfig.theme.screens.lg})`
  );

  let slidesToShow;
  if (isSm) slidesToShow = 1;
  if (isMedium) slidesToShow = 2;
  if (isLarge) slidesToShow = 3;

  return (
    <div
      id="reviews-section"
      className={classNames(reviews ? 'bg-dark' : 'bg-secondary', ' ')}
    >
      <div className="relative">
        <div className="absolute right-16 -top-96 opacity-30">
          <StaticImage
            src="../../images/assets/steps-grayscale.png"
            alt="asset"
            width={300}
          />
        </div>
      </div>
      <div className="w-full py-16 mx-auto lg:max-w-screen-2xl lg:py-24">
        <div className="relative mx-auto overflow-hidden">
          <h2 className="font-serif text-3xl font-normal text-center md:text-5xl text-highlight-ochre">
            {reviews.title}
          </h2>

          <div className="mt-12">
            <Carousel
              autoplay
              autoplayInterval={3000}
              cellAlign="center"
              dragging
              easing="easeCubicInOut"
              enableKeyboardControls
              slidesToShow={slidesToShow}
              swiping
              wrapAround
              renderCenterLeftControls={({ previousSlide }) => (
                <button
                  aria-label="Next slide"
                  className="p-4"
                  onClick={previousSlide}
                  type="button"
                >
                  <FiChevronLeft className="text-4xl text-white md:text-6xl" />
                </button>
              )}
              renderCenterRightControls={({ nextSlide }) => (
                <button
                  aria-label="Next slide"
                  className="p-4"
                  onClick={nextSlide}
                  type="button"
                >
                  <FiChevronRight className="text-4xl text-white md:text-6xl" />
                </button>
              )}
              renderBottomCenterControls={null}
              className="px-12 mt-6 md:my-12"
            >
              {reviews.reviewItems.map((item) => (
                <div
                  key={item.id}
                  className="flex flex-col items-center w-64 max-w-xs px-8 pt-10 mx-auto rounded-lg md:w-72 h-80 bg-reviews-bg"
                >
                  <Stars rating={item.rating} />
                  <div className="mt-2 text-sm leading-7 tracking-wider text-center text-light">
                    {item.reviewDescription}
                  </div>
                </div>
              ))}
            </Carousel>
          </div>
        </div>
      </div>
    </div>
  );
};

const ReviewsCorporate = ({ reviews, title }: ReviewsProps): JSX.Element => {
  const tailwindConfig = useTailwindConfig();
  const isMedium = useMediaQuery(
    `(min-width: ${tailwindConfig.theme.screens.md})`
  );
  const isSm = useMediaQuery(`(min-width: ${tailwindConfig.theme.screens.sm})`);
  const isLarge = useMediaQuery(
    `(min-width: ${tailwindConfig.theme.screens.lg})`
  );
  let slidesToShow;
  if (isSm) slidesToShow = 1;
  if (isMedium) slidesToShow = 2;
  if (isLarge) slidesToShow = 4;

  return (
    <div
      id="reviews-section"
      className={classNames(reviews ? 'bg-dark' : 'bg-secondary', ' ')}
    >
      <div className="relative">
        <div className="absolute inset-0 h-screen">
          <StaticImage
            src="../../images/assets/service-grid-bg.png"
            alt="asset"
            layout="fullWidth"
          />
        </div>
      </div>
      <div className="w-full py-16 mx-auto lg:max-w-screen-2xl lg:py-24">
        <div className="relative mx-auto overflow-hidden">
          <h2 className="font-serif text-5xl font-normal text-center text-highlight-ochre">
            {reviews.title}
          </h2>
          <div className="mt-12">
            <Carousel
              autoplay={false}
              autoplayInterval={3000}
              dragging={false}
              easing="easeCubicInOut"
              enableKeyboardControls
              slidesToShow={slidesToShow}
              swiping={false}
              wrapAround
              renderCenterLeftControls={null}
              renderCenterRightControls={null}
              renderBottomCenterControls={null}
              className="px-12 mt-6 md:my-12"
            >
              {reviews.reviewItems.map((item) => (
                <div
                  key={item.id}
                  className="flex flex-col items-center w-auto p-8 pt-10 mx-auto rounded-lg md:p-8 sm:mx-8 bg-reviews-bg"
                >
                  <Stars rating={item.rating} />
                  <div className="font-serif text-xl text-center text-highlight-ochre xl:text-3xl md:my-6">
                    {item.reviewTitle}
                  </div>
                  <div className="overflow-y-hidden text-sm tracking-wider text-center md:leading-7 h-72 2xl:h-60">
                    {item.reviewDescription}
                  </div>
                </div>
              ))}
            </Carousel>
          </div>
        </div>
      </div>
    </div>
  );
};

export { Reviews };
